/**
 * MyDay — Physician Command Center
 *
 * Thin orchestrator: loads data, renders the Provider Queue panel,
 * bucket bar, and active work area. All sub-components extracted
 * to /components/my_day/.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Loader2, ListChecks, Plus, Sparkles, Command } from 'lucide-react';
import { toast } from 'sonner';

// Sub-components
import { ProviderQueuePanel } from '../components/my_day/ProviderQueuePanel';
import { BucketBar }          from '../components/my_day/BucketBar';
import { WorkItem }            from '../components/my_day/WorkItem';
import { EmptyBucket }         from '../components/my_day/WorkItemForms';
import { NewActionPanel }      from '../components/my_day/QuickActions';

// Bucket views (dedicated feature pages)
import LabsImagingView          from './LabsImagingView';
import ProcedurePendingQueue     from '../components/ProcedurePendingQueue';
import { MessagesBucketView }   from './MessagesBucket';
import { MyDocumentsBucketView } from './DocumentsBucket';
import { DelegatedBucketView }  from './DelegatedBucket';
import { RefillBucketView }     from './RefillBucket';

export default function MyDay() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [roomsCollapsed, setRoomsCollapsed] = useState(false);
  const [sideView, setSideView] = useState('queue');
  const [activeBucket, setActiveBucket] = useState('messages');
  const [bucketSummary, setBucketSummary] = useState({ lab_xray: 0, task: 0, correspondence: 0 });
  const [items, setItems] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showNewAction, setShowNewAction] = useState(false);

  // 7K: Deep link — auto-open a specific conversation or document
  const [autoOpenConvId, setAutoOpenConvId] = useState(null);
  const [autoOpenCorrId, setAutoOpenCorrId] = useState(null);
  const deepLinkConv = searchParams.get('conv');
  const deepLinkCorr = searchParams.get('corr');

  useEffect(() => {
    const load = async () => {
      try {
        const [roomsRes, summaryRes, convRes, docRes, delegatedRes] = await Promise.all([
          api.get('/my-day/rooms'),
          api.get('/my-day/buckets/summary'),
          api.get('/conversations').catch(() => ({ data: { count: 0 } })),
          api.get('/documents/my-queue').catch(() => ({ data: { count: 0 } })),
          api.get('/my-day/delegated').catch(() => ({ data: { total: 0 } })),
        ]);
        setRooms(roomsRes.data.rooms || []);
        const summary = summaryRes.data;
        summary.messages = convRes.data.count || 0;
        summary.my_documents = docRes.data.count || 0;
        summary.delegated = delegatedRes.data.total || 0;
        setBucketSummary(summary);
      } catch (err) {
        console.error('Failed to load My Day data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (deepLinkConv) {
      setActiveBucket('messages');
      setAutoOpenConvId(deepLinkConv);
      setSearchParams(prev => { prev.delete('conv'); return prev; }, { replace: true });
    } else if (deepLinkCorr) {
      setActiveBucket('my_documents');
      setAutoOpenCorrId(deepLinkCorr);
      setSearchParams(prev => { prev.delete('corr'); return prev; }, { replace: true });
    }
  }, [loading, deepLinkConv, deepLinkCorr, setSearchParams]);

  const loadBucketItems = useCallback(async (bucket) => {
    // These buckets have dedicated view components — no generic items fetch needed
    const DEDICATED_BUCKETS = ['messages', 'my_documents', 'delegated', 'lab_xray', 'prescriptions'];
    if (DEDICATED_BUCKETS.includes(bucket)) {
      setItems([]);
      return;
    }
    setItemsLoading(true);
    try {
      const res = await api.get(`/my-day/buckets/${bucket}`);
      setItems(res.data.items || []);
    } catch {
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  }, []);

  useEffect(() => { loadBucketItems(activeBucket); }, [activeBucket, loadBucketItems]);

  const refreshAll = async () => {
    const [summaryRes] = await Promise.all([api.get('/my-day/buckets/summary')]);
    setBucketSummary(summaryRes.data);
    loadBucketItems(activeBucket);
  };

  const handleSeedDemo = async () => {
    setSeeding(true);
    try { await api.post('/my-day/seed-demo'); await refreshAll(); }
    catch (err) { console.error('Seed failed:', err); }
    finally { setSeeding(false); }
  };

  const totalPending = (bucketSummary.messages || 0) + (bucketSummary.prescriptions || 0) +
    (bucketSummary.lab_xray || 0) + (bucketSummary.my_documents || 0) + (bucketSummary.delegated || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="my-day-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden" data-testid="my-day-page">
      {/* LEFT PANEL — Provider Queue */}
      <ProviderQueuePanel
        rooms={rooms}
        collapsed={roomsCollapsed}
        onToggle={() => setRoomsCollapsed(p => !p)}
        sideView={sideView}
        onSwitchView={setSideView}
        onPatientClick={(room) => {
          if (room.encounter_id) navigate(`/visit/${room.encounter_id}`);
          else navigate(`/chart/${room.patient_id}`);
        }}
        onForceStart={async (room) => {
          try {
            const res = await api.post('/encounters/force-start', { appointment_id: room.id, reason: 'Provider direct start' });
            navigate(`/visit/${res.data.encounter.id}`);
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to start visit');
          }
        }}
      />

      {/* MAIN WORKSPACE */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-100/60">
        {/* Header / command strip */}
        <div className="mx-4 mt-3 mb-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="my-day-header">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>My Day</h1>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {totalPending > 0 ? `${totalPending} items need attention` : 'All clear'}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              {totalPending === 0 && (
                <Button variant="ghost" size="sm" className="text-[10px] gap-1 text-slate-400"
                  onClick={handleSeedDemo} disabled={seeding} data-testid="seed-demo-btn">
                  {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Demo
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs text-slate-600"
                onClick={() => navigate('/orders')} data-testid="orders-workspace-btn">
                <ListChecks className="w-3.5 h-3.5" /> Orders
              </Button>
              <Button size="sm" className="h-7 gap-1.5 text-xs"
                onClick={() => setShowNewAction(true)} data-testid="new-order-btn">
                <Plus className="w-3.5 h-3.5" /> New Order
              </Button>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Command className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
              <input type="text"
                placeholder="Quick command: 'Refill lisinopril 10mg' or 'Order CBC and CMP'..."
                className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 placeholder:text-slate-300 focus:bg-white focus:border-slate-300 transition-colors"
                data-testid="quick-command-input" />
            </div>
            <Button variant="outline" size="sm" className="rounded-xl text-xs gap-1 text-slate-500 border-slate-200">
              <Sparkles className="w-3.5 h-3.5" /> Parse
            </Button>
          </div>
        </div>

        {showNewAction && (
          <NewActionPanel onClose={() => setShowNewAction(false)} onComplete={() => { setShowNewAction(false); refreshAll(); }} />
        )}

        <BucketBar activeBucket={activeBucket} summary={bucketSummary} onSelect={setActiveBucket} />

        {/* Active work area */}
        <div className="flex-1 overflow-y-auto px-6 pb-6" data-testid="work-area">
          {activeBucket === 'prescriptions' ? (
            <RefillBucketView onComplete={refreshAll} />
          ) : activeBucket === 'messages' ? (
            <MessagesBucketView onComplete={refreshAll} autoOpenId={autoOpenConvId} onAutoOpenConsumed={() => setAutoOpenConvId(null)} />
          ) : activeBucket === 'my_documents' ? (
            <MyDocumentsBucketView />
          ) : activeBucket === 'delegated' ? (
            <DelegatedBucketView />
          ) : activeBucket === 'lab_xray' ? (
            <LabsImagingView onComplete={refreshAll} />
          ) : activeBucket === 'procedures' ? (
            <ProcedurePendingQueue onDocumented={refreshAll} />
          ) : itemsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <EmptyBucket bucket={activeBucket} onSeed={handleSeedDemo} seeding={seeding} />
          ) : (
            <div className="space-y-3 mt-1">
              {items.map(item => (
                <WorkItem
                  key={item.id}
                  item={item}
                  bucket={activeBucket}
                  expanded={expandedItem === item.id}
                  onToggle={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                  onComplete={() => { setExpandedItem(null); refreshAll(); }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
